// 文字量測與換行：以實際字型檔量測，拉丁字用 Excalifont、中日韓字用 Xiaolai。
import * as fontkit from 'fontkit';

type Font = fontkit.Font;
export type FontKey = 'latin' | 'cjk';

export interface FontSet {
  latin: Font;
  cjk: Font;
  cache: Map<string, number>;
}

export interface PlacedGlyph {
  font: FontKey;
  glyphId: number;
  /** 字形原點（基線）在畫布上的位置 */
  x: number;
  y: number;
  /** 字型單位 → 像素的縮放 */
  scale: number;
}

export interface TextLine {
  text: string;
  width: number;
}

export interface TextBlock {
  lines: TextLine[];
  width: number;
  height: number;
  fontSize: number;
  lineHeight: number;
}

export const LINE_HEIGHT = 1.25;
// 基線位置：字身框頂端往下的比例（兩套字型的視覺折衷值，經目視調整）。
const BASELINE_RATIO = 0.8;
const CJK = /[⺀-⿟　-〿぀-ヿ㄀-ㄯ㆐-ㇿ㐀-䶿一-鿿豈-﫿︰-﹏＀-￯]/;
// 不可出現在行首的標點（附到前一個字）
const NO_LINE_START = new Set([...'，。、；：！？）」』】》〉〕｝’”…‥・ー々,.;:!?)]}%\'"']);
// 不可出現在行尾的標點（附到下一個字）
const NO_LINE_END = new Set([...'（「『【《〈〔｛‘“([{$#']);

// 由字型位元組建立字型組。
export function createFontSet(bytes: { latin: Uint8Array; cjk: Uint8Array }): FontSet {
  const open = (data: Uint8Array): Font => {
    // Node 的 fontkit 需要 Buffer；瀏覽器版直接接受 Uint8Array。
    const input = typeof Buffer !== 'undefined' ? Buffer.from(data.buffer, data.byteOffset, data.byteLength) : data;
    const font = fontkit.create(input as Buffer);
    if (!('layout' in font)) throw new Error('字型檔不可為字型集合（TTC/DFONT）');
    return font as Font;
  };
  return { latin: open(bytes.latin), cjk: open(bytes.cjk), cache: new Map() };
}

// 決定單一字元使用哪套字型；兩套都沒有時回傳 null。
export function fontFor(fonts: FontSet, char: string): FontKey | null {
  const codePoint = char.codePointAt(0)!;
  if (!CJK.test(char) && fonts.latin.hasGlyphForCodePoint(codePoint)) return 'latin';
  if (fonts.cjk.hasGlyphForCodePoint(codePoint)) return 'cjk';
  if (fonts.latin.hasGlyphForCodePoint(codePoint)) return 'latin';
  return null;
}

// 回傳字型不支援的字元（去重），供 validate 提醒。
export function missingGlyphs(fonts: FontSet, text: string): string[] {
  const missing = new Set<string>();
  for (const char of text) if (char.trim() && !fontFor(fonts, char)) missing.add(char);
  return [...missing];
}

// 把文字切成同一字型的連續片段；缺字直接略過。
function splitRuns(fonts: FontSet, text: string): { font: FontKey; text: string }[] {
  const runs: { font: FontKey; text: string }[] = [];
  for (const char of text) {
    // 空白跟隨前一段字型，避免拆出過多片段。
    const font = char === ' ' && runs.length ? runs[runs.length - 1]!.font : fontFor(fonts, char);
    if (!font) continue;
    const last = runs[runs.length - 1];
    if (last && last.font === font) last.text += char;
    else runs.push({ font, text: char });
  }
  return runs;
}

// 量測單行文字寬度（像素），結果快取。
export function measure(fonts: FontSet, text: string, fontSize: number): number {
  const key = `${fontSize}|${text}`;
  const cached = fonts.cache.get(key);
  if (cached !== undefined) return cached;
  let width = 0;
  for (const run of splitRuns(fonts, text)) {
    const font = fonts[run.font];
    const glyphRun = font.layout(run.text);
    const scale = fontSize / font.unitsPerEm;
    for (const position of glyphRun.positions) width += position.xAdvance * scale;
  }
  fonts.cache.set(key, width);
  return width;
}

// 切成不可再分的換行單位：中日韓逐字、拉丁逐詞，並套用標點禁則。
export function tokenize(text: string): string[] {
  const tokens: string[] = [];
  let word = '';
  const flush = () => { if (word) { tokens.push(word); word = ''; } };
  for (const char of text) {
    if (char === ' ') { word += char; flush(); continue; }
    if (CJK.test(char)) {
      flush();
      if (NO_LINE_START.has(char) && tokens.length) tokens[tokens.length - 1] += char;
      else tokens.push(char);
      continue;
    }
    if (NO_LINE_START.has(char) && !word && tokens.length) { tokens[tokens.length - 1] += char; continue; }
    word += char;
  }
  flush();
  // 行尾禁則：開括號類與下一個單位合併。
  const merged: string[] = [];
  for (const token of tokens) {
    const previous = merged[merged.length - 1];
    if (previous !== undefined && NO_LINE_END.has([...previous].pop()!)) merged[merged.length - 1] = previous + token;
    else merged.push(token);
  }
  return merged;
}

// 依最大寬度換行，回傳各行與整體尺寸。
export function layoutText(fonts: FontSet, text: string, fontSize: number, maxWidth: number): TextBlock {
  const lines: string[] = [];
  let current = '';
  const pushLine = () => { if (current.trim()) lines.push(current.trim()); current = ''; };
  for (const token of tokenize(text)) {
    const candidate = current + token;
    if (!current || measure(fonts, candidate.trimEnd(), fontSize) <= maxWidth) {
      current = candidate;
      // 單一單位就超寬（長英文字）：逐字硬切。
      if (measure(fonts, current.trimEnd(), fontSize) > maxWidth && [...current.trim()].length > 1) {
        const chars = [...current];
        current = '';
        for (const char of chars) {
          if (current && measure(fonts, (current + char).trimEnd(), fontSize) > maxWidth) pushLine();
          current += char;
        }
      }
      continue;
    }
    pushLine();
    current = token;
  }
  pushLine();
  if (!lines.length) lines.push('');
  const measured = lines.map(line => ({ text: line, width: measure(fonts, line, fontSize) }));
  const lineHeight = fontSize * LINE_HEIGHT;
  return {
    lines: measured,
    width: Math.max(...measured.map(line => line.width)),
    height: lineHeight * measured.length,
    fontSize,
    lineHeight,
  };
}

// 計算文字區塊中每個字形的位置；(left, top) 為區塊左上角，align 決定每行水平對齊。
export function placeGlyphs(fonts: FontSet, block: TextBlock, left: number, top: number, align: 'left' | 'center' = 'center'): PlacedGlyph[] {
  const placed: PlacedGlyph[] = [];
  block.lines.forEach((line, index) => {
    let x = left + (align === 'center' ? (block.width - line.width) / 2 : 0);
    const baseline = top + index * block.lineHeight + (block.lineHeight - block.fontSize) / 2 + block.fontSize * BASELINE_RATIO;
    for (const run of splitRuns(fonts, line.text)) {
      const font = fonts[run.font];
      const scale = block.fontSize / font.unitsPerEm;
      const glyphRun = font.layout(run.text);
      glyphRun.glyphs.forEach((glyph, glyphIndex) => {
        const position = glyphRun.positions[glyphIndex]!;
        if (glyph.path.commands.length) placed.push({ font: run.font, glyphId: glyph.id, x: x + position.xOffset * scale, y: baseline - position.yOffset * scale, scale });
        x += position.xAdvance * scale;
      });
    }
  });
  return placed;
}

// 取得字形輪廓（字型單位，y 向上），輸出為 SVG path；座標取整數。
export function glyphPathData(fonts: FontSet, font: FontKey, glyphId: number): string {
  const glyph = fonts[font].getGlyph(glyphId);
  const parts: string[] = [];
  const round = (value: number) => String(Math.round(value));
  for (const { command, args } of glyph.path.commands) {
    const points = args.map((value, index) => round(index % 2 ? -value : value)).join(' ');
    switch (command) {
      case 'moveTo': parts.push('M' + points); break;
      case 'lineTo': parts.push('L' + points); break;
      case 'quadraticCurveTo': parts.push('Q' + points); break;
      case 'bezierCurveTo': parts.push('C' + points); break;
      case 'closePath': parts.push('Z'); break;
    }
  }
  return parts.join('');
}
